import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, FileText, Trash2, List, Edit } from 'lucide-react';
import { effectsAPI, placedEffectsAPI, pdfAPI, riskEntriesAPI } from '../services/api';
import toast from 'react-hot-toast';
import DocumentUpload from './DocumentUpload';
import EffectSelectionModal from './EffectSelectionModal';

// Editable Risk Entry Row Component - matches original PyQt6 table
const RiskEntryRow = ({ entry, index, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState({});
  const [editValues, setEditValues] = useState(entry);

  const probabilityOptions = ['Lav', 'Middels', 'Høy', 'Meget høy'];
  const consequenceOptions = ['Lav', 'Middels', 'Høy', 'Meget høy'];
  const riskLevelOptions = ['Ok', 'Lav', 'Middels', 'Høy', 'Meget høy'];

  const startEdit = (field) => {
    setIsEditing({ ...isEditing, [field]: true });
  };

  const saveEdit = async (field) => {
    try {
      const response = await riskEntriesAPI.update(entry.id, editValues);
      onUpdate(response.data);
      setIsEditing({ ...isEditing, [field]: false });
      toast.success('Risk entry updated');
    } catch (error) {
      toast.error('Failed to update risk entry');
      console.error('Error updating risk entry:', error);
    }
  };

  const cancelEdit = (field) => {
    setEditValues({ ...editValues, [field]: entry[field] });
    setIsEditing({ ...isEditing, [field]: false });
  };

  const handleChange = (field, value) => {
    setEditValues({ ...editValues, [field]: value });
  };

  const handleKeyPress = (e, field) => {
    if (e.key === 'Enter') {
      saveEdit(field);
    } else if (e.key === 'Escape') {
      cancelEdit(field);
    }
  };

  // Color scheme from original PyQt6 app
  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'Ok': return '#27ae60'; // darker green matching PDF
      case 'Lav': return '#32CD32'; // green  
      case 'Middels': return '#FFFF00'; // yellow
      case 'Høy': return '#FFA500'; // orange
      case 'Meget høy': return '#FF0000'; // red
      default: return 'transparent';
    }
  };

  const getProbabilityConsequenceColor = (level) => {
    switch (level) {
      case 'Lav': return '#32CD32'; // green
      case 'Middels': return '#FFFF00'; // yellow  
      case 'Høy': return '#FFA500'; // orange
      case 'Meget høy': return '#FF0000'; // red
      default: return 'transparent';
    }
  };

  const renderEditableCell = (field, value, options = null) => {
    if (isEditing[field]) {
      if (options) {
        // Dropdown for predefined options
        return (
          <select
            value={editValues[field]}
            onChange={(e) => handleChange(field, e.target.value)}
            onBlur={() => saveEdit(field)}
            onKeyDown={(e) => handleKeyPress(e, field)}
            style={{
              width: '100%',
              padding: '4px',
              border: '1px solid #007bff',
              borderRadius: '4px',
              fontSize: '14px'
            }}
            autoFocus
          >
            {options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      } else {
        // Text input/textarea for free text
        const isLongText = field === 'hazard' || field === 'cause' || field === 'measures';
        const InputComponent = isLongText ? 'textarea' : 'input';
        
        return (
          <InputComponent
            type={isLongText ? undefined : 'text'}
            value={editValues[field]}
            onChange={(e) => handleChange(field, e.target.value)}
            onBlur={() => saveEdit(field)}
            onKeyDown={(e) => handleKeyPress(e, field)}
            style={{
              width: '100%',
              padding: '4px',
              border: '1px solid #007bff',
              borderRadius: '4px',
              fontSize: '14px',
              resize: isLongText ? 'vertical' : 'none',
              minHeight: isLongText ? '60px' : 'auto'
            }}
            autoFocus
          />
        );
      }
    } else {
      // Display mode with proper colors
      let backgroundColor = 'transparent';
      if (field === 'probability' || field === 'consequence') {
        backgroundColor = getProbabilityConsequenceColor(value);
      } else if (field === 'risk_level') {
        backgroundColor = getRiskLevelColor(value);
      }

      return (
        <div
          onClick={() => startEdit(field)}
          style={{
            padding: '4px',
            minHeight: '20px',
            cursor: 'pointer',
            borderRadius: '4px',
            border: '1px solid transparent',
            backgroundColor: backgroundColor,
            color: backgroundColor === '#FFFF00' ? '#000' : (backgroundColor && backgroundColor !== 'transparent' ? '#000' : 'inherit')
          }}
          onMouseEnter={(e) => {
            if (backgroundColor === 'transparent') {
              e.target.style.backgroundColor = '#f8f9fa';
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = backgroundColor;
          }}
          title="Click to edit"
        >
          {value || ''}
        </div>
      );
    }
  };

  const handleDelete = async () => {
    try {
      await riskEntriesAPI.delete(entry.id);
      onDelete(entry.id);
      toast.success('Risk entry deleted');
    } catch (error) {
      toast.error('Failed to delete risk entry');
      console.error('Error deleting risk entry:', error);
    }
  };

  return (
    <tr>
      <td style={{ padding: '6px 4px', textAlign: 'center' }}>
        <button
          className="btn btn-danger btn-sm"
          onClick={handleDelete}
          title="Delete risk entry"
          style={{ padding: '4px 6px', fontSize: '11px' }}
        >
          <Trash2 size={12} />
        </button>
      </td>
      <td style={{ padding: '6px 6px' }}>{renderEditableCell('activity', editValues.activity)}</td>
      <td style={{ padding: '6px 6px' }}>{renderEditableCell('hazard', editValues.hazard)}</td>
      <td style={{ padding: '6px 6px' }}>{renderEditableCell('cause', editValues.cause)}</td>
      <td style={{ padding: '6px 6px' }}>{renderEditableCell('probability', editValues.probability, probabilityOptions)}</td>
      <td style={{ padding: '6px 6px' }}>{renderEditableCell('consequence', editValues.consequence, consequenceOptions)}</td>
      <td style={{ padding: '6px 6px' }}>{renderEditableCell('measures', editValues.measures)}</td>
      <td style={{ padding: '6px 6px' }}>{renderEditableCell('risk_level', editValues.risk_level, riskLevelOptions)}</td>
    </tr>
  );
};

// CACHE BUST: 2025-07-25-16:10 - NO CANVAS VERSION
const RiskAnalysis = ({ event, onBack, onEditEvent }) => {
  // Add progress bar animation styles
  const progressBarStyle = `
    @keyframes progress-bar {
      0% { transform: translateX(-100%); }
      50% { transform: translateX(0%); }
      100% { transform: translateX(100%); }
    }
  `;

  // Inject the styles
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = progressBarStyle;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  const [effects, setEffects] = useState([]);
  const [placedEffects, setPlacedEffects] = useState([]);
  const [riskEntries, setRiskEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEffectSelection, setShowEffectSelection] = useState(false);
  const [pdfLoading, setPdfLoading] = useState({ riskAnalysis: false, effectsList: false });

  useEffect(() => {
    if (event) {
      loadData();
    }
  }, [event]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load all effects for selection
      const effectsResponse = await effectsAPI.getAll();
      setEffects(effectsResponse.data);
      
      // Load placed effects for this event
      const placedResponse = await placedEffectsAPI.getForEvent(event.id);
      setPlacedEffects(placedResponse.data);
      
      // Load risk entries for this event
      try {
        const riskResponse = await riskEntriesAPI.getForEvent(event.id);
        setRiskEntries(riskResponse.data);
      } catch (error) {
        // Risk entries might not exist yet, that's okay
        console.log('No risk entries found for event:', error);
        setRiskEntries([]);
      }
    } catch (error) {
      toast.error('Failed to load data');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEffect = async (effectId, quantity = 1, selectedVariant = null) => {
    try {
      const placedEffectData = {
        effect_id: effectId,
        x_position: 0,
        y_position: 0,
        rotation: 0,
        scale: 1,
        quantity: quantity,
        selected_variant: selectedVariant
      };
      
      const response = await placedEffectsAPI.create(event.id, placedEffectData);
      setPlacedEffects(prev => [...prev, response.data]);
      toast.success(`Effect added to event${selectedVariant ? ` (${selectedVariant})` : ''}`);
      setShowEffectSelection(false);
    } catch (error) {
      toast.error('Failed to add effect');
      console.error('Error adding effect:', error);
    }
  };

  const handleRemoveEffect = async (placedEffectId) => {
    try {
      await placedEffectsAPI.delete(placedEffectId);
      setPlacedEffects(prev => prev.filter(pe => pe.id !== placedEffectId));
      toast.success('Effect removed from event');
    } catch (error) {
      toast.error('Failed to remove effect');
      console.error('Error removing effect:', error);
    }
  };

  // Helper function to get variant-specific values or fall back to base effect values
  const getEffectValue = (placedEffect, property) => {
    if (placedEffect.selected_variant && placedEffect.effect.variants) {
      try {
        const variants = JSON.parse(placedEffect.effect.variants);
        const selectedVariant = variants.find(v => v.name === placedEffect.selected_variant);
        if (selectedVariant && selectedVariant[property] !== undefined) {
          return selectedVariant[property];
        }
      } catch (e) {
        console.error('Error parsing variants:', e);
      }
    }
    // Fall back to base effect value
    return placedEffect.effect[property];
  };

  // Helper function to check if value comes from variant
  const isVariantValue = (placedEffect, property) => {
    if (placedEffect.selected_variant && placedEffect.effect.variants) {
      try {
        const variants = JSON.parse(placedEffect.effect.variants);
        const selectedVariant = variants.find(v => v.name === placedEffect.selected_variant);
        return selectedVariant && selectedVariant[property] !== undefined;
      } catch (e) {
        return false;
      }
    }
    return false;
  };

  const generateRiskEntries = async () => {
    if (placedEffects.length === 0) {
      toast.error('No effects to generate risk entries for');
      return;
    }

    try {
      // Include both effect IDs and variant information
      const effectData = placedEffects.map(pe => ({
        effect_id: pe.effect_id,
        selected_variant: pe.selected_variant,
        quantity: pe.quantity || 1
      }));
      
      await riskEntriesAPI.generateForEvent(event.id, effectData);
      
      // Reload risk entries
      const riskResponse = await riskEntriesAPI.getForEvent(event.id);
      setRiskEntries(riskResponse.data);
      
      toast.success('Risk entries generated successfully');
    } catch (error) {
      toast.error('Failed to generate risk entries');
      console.error('Error generating risk entries:', error);
    }
  };

  const addManualRiskEntry = async () => {
    try {
      const newEntry = {
        event_id: event.id,
        activity: 'Ny aktivitet',
        hazard: 'Beskrivelse av faremoment',
        cause: 'Beskrivelse av årsak',
        probability: 'Lav',
        consequence: 'Lav',
        measures: 'Beskrivelse av tiltak',
        risk_level: 'Ok'
      };
      
      const response = await riskEntriesAPI.create(newEntry);
      setRiskEntries(prev => [...prev, response.data]);
      toast.success('New risk entry added');
    } catch (error) {
      toast.error('Failed to add risk entry');
      console.error('Error adding risk entry:', error);
    }
  };

  const clearAllRiskEntries = async () => {
    if (riskEntries.length === 0) {
      return;
    }

    try {
      // Delete all risk entries for this event
      for (const entry of riskEntries) {
        await riskEntriesAPI.delete(entry.id);
      }
      
      setRiskEntries([]);
      toast.success('All risk entries cleared');
    } catch (error) {
      toast.error('Failed to clear risk entries');
      console.error('Error clearing risk entries:', error);
    }
  };

  const generateRiskAnalysisPDF = async () => {
    if (riskEntries.length === 0) {
      toast.error('No risk entries found. Please generate risk entries first.');
      return;
    }

    try {
      setPdfLoading(prev => ({ ...prev, riskAnalysis: true }));
      const response = await pdfAPI.generateRiskAnalysisPDF(event.id);
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${event.name.replace(/\s+/g, '_')}_-_ROS-Analyse_-_${new Date().toLocaleDateString('no-NO').replace(/\./g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Risk analysis PDF downloaded!');
    } catch (error) {
      toast.error('Failed to generate risk analysis PDF');
      console.error('Error generating PDF:', error);
    } finally {
      setPdfLoading(prev => ({ ...prev, riskAnalysis: false }));
    }
  };

  const generateEffectsListPDF = async () => {
    if (placedEffects.length === 0) {
      toast.error('No effects found for this event');
      return;
    }

    try {
      setPdfLoading(prev => ({ ...prev, effectsList: true }));
      const response = await pdfAPI.generateEffectsListPDF(event.id);
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${event.name.replace(/\s+/g, '_')}_-_Effektliste_-_${new Date().toLocaleDateString('no-NO').replace(/\./g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Effects list PDF downloaded!');
    } catch (error) {
      toast.error('Failed to generate effects list PDF');
      console.error('Error generating PDF:', error);
    } finally {
      setPdfLoading(prev => ({ ...prev, effectsList: false }));
    }
  };

  if (!event) {
    return (
      <div className="text-center">
        <p>No event selected</p>
        <button className="btn" onClick={onBack}>
          Back to Events
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center">
        <p>Loading risk analysis...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <button className="btn btn-secondary" onClick={onBack} title="Back to events">
              <ArrowLeft size={16} />
            </button>
            <div>
              <h2 style={{ margin: '0' }}>Risk Analysis</h2>
              <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>{event.name}</p>
            </div>
          </div>
          <div className="pdf-buttons" style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={onEditEvent}>
              <Edit size={15} /> Edit Event
            </button>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <button 
                className="btn btn-secondary"
                onClick={generateRiskAnalysisPDF}
                disabled={riskEntries.length === 0 || pdfLoading.riskAnalysis}
                style={{ minWidth: '160px' }}
              >
                <FileText size={16} className="mr-2" />
                {pdfLoading.riskAnalysis ? 'Generating...' : 'Risk Analysis PDF'}
              </button>
              {pdfLoading.riskAnalysis && (
                <div style={{ width: '100%', height: '4px', backgroundColor: '#e2e8f0', borderRadius: '2px', overflow: 'hidden' }}>
                  <div 
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      backgroundColor: '#3b82f6', 
                      animation: 'progress-bar 2s ease-in-out infinite' 
                    }}
                  />
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <button 
                className="btn btn-secondary"
                onClick={generateEffectsListPDF}
                disabled={placedEffects.length === 0 || pdfLoading.effectsList}
                style={{ minWidth: '160px' }}
              >
                <List size={16} className="mr-2" />
                {pdfLoading.effectsList ? 'Generating...' : 'Effects List PDF'}
              </button>
              {pdfLoading.effectsList && (
                <div style={{ width: '100%', height: '4px', backgroundColor: '#e2e8f0', borderRadius: '2px', overflow: 'hidden' }}>
                  <div 
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      backgroundColor: '#3b82f6', 
                      animation: 'progress-bar 2s ease-in-out infinite' 
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Effects Selection Modal */}
        {showEffectSelection && (
          <EffectSelectionModal
            effects={effects}
            onSelectEffect={handleAddEffect}
            onClose={() => setShowEffectSelection(false)}
          />
        )}

        {/* Event Details and Risk Summary - ABOVE ENTRIES */}
        <div className="event-risk-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', margin: '20px 0' }}>
          {/* Event Details */}
          <div className="card" style={{ margin: '0' }}>
            <h3 style={{ marginBottom: '15px' }}>Event Details</h3>
            <div style={{ fontSize: '14px' }}>
              <div style={{ marginBottom: '10px' }}>
                <strong>Date:</strong> {new Date(event.date).toLocaleDateString()}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Location:</strong> {event.location || 'Not specified'}
              </div>
              {event.description && (
                <div style={{ marginBottom: '10px' }}>
                  <strong>Description:</strong> {event.description}
                </div>
              )}
              <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                <strong>Venue Size:</strong> 
                {event.venue_width && event.venue_depth && event.venue_height ? (
                  <span style={{ marginLeft: '5px' }}>
                    L {event.venue_width}m × D {event.venue_depth}m × H {event.venue_height}m
                  </span>
                ) : (
                  <span style={{ marginLeft: '5px', display: 'flex', alignItems: 'center', color: '#ef4444' }}>
                    <span style={{ marginRight: '4px' }}>Warning:</span>
                    Not specified
                  </span>
                )}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Leverandør:</strong> {event.supplier || 'Not specified'}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Ansvarlig Pyrotekniker:</strong> {event.responsible_technician || 'Not specified'}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Documents:</strong>
                <DocumentUpload eventId={event.id} compact={true} />
              </div>
            </div>
          </div>

          {/* Risk Analysis Summary */}
          <div className="card" style={{ margin: '0' }}>
            <h3 style={{ marginBottom: '15px' }}>Risk Analysis Summary</h3>
            <div style={{ fontSize: '14px' }}>
              <div style={{ marginBottom: '10px' }}>
                <strong>Total Effects:</strong> {placedEffects.length}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Risk Entries:</strong> {riskEntries.length}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Max Safety Distance:</strong> {
                  placedEffects.length > 0 
                    ? Math.max(...placedEffects.map(pe => getEffectValue(pe, 'safety_distance') || 0)) + 'm'
                    : 'N/A'
                }
              </div>
              {riskEntries.length > 0 && (
                <div style={{ marginTop: '15px' }}>
                  <strong>Risk Level Distribution:</strong>
                  <div style={{ marginTop: '5px', fontSize: '13px' }}>
                    {['Ok', 'Lav', 'Middels', 'Høy', 'Meget høy'].map(level => {
                      const count = riskEntries.filter(entry => entry.risk_level === level).length;
                      return count > 0 ? (
                        <div key={level} style={{ marginBottom: '3px' }}>
                          • {level}: {count}
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Event Effects List - ABOVE Risk Entries */}
        <div className="card" style={{ margin: '20px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: '0' }}>Event Effects ({placedEffects.length})</h3>
            <button 
              className="btn btn-sm"
              onClick={() => setShowEffectSelection(!showEffectSelection)}
            >
              <Plus size={16} className="mr-2" />
              {showEffectSelection ? 'Hide Effects' : 'Add Effects'}
            </button>
          </div>
          
          {placedEffects.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <p>No effects added yet.</p>
              <p>Click "Add Effects" to start building your event.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-striped" style={{ minWidth: '800px', fontSize: '14px' }}>
                <thead style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <tr>
                    <th style={{ padding: '12px 8px' }}>Name</th>
                    <th style={{ padding: '12px 8px' }}>Type</th>
                    <th style={{ padding: '12px 8px' }}>Quantity</th>
                    <th style={{ padding: '12px 8px' }}>Effective Range</th>
                    <th style={{ padding: '12px 8px' }}>Safety Distance</th>
                    <th style={{ padding: '12px 8px' }}>Clearance Height</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {placedEffects.map((placedEffect, index) => (
                    <tr key={placedEffect.id}>
                      <td style={{ padding: '8px', fontWeight: '600' }}>
                        {placedEffect.effect.name}
                        {placedEffect.selected_variant && (
                          <div style={{ fontSize: '12px', fontWeight: '400', color: '#3b82f6', marginTop: '2px' }}>
                            {placedEffect.selected_variant}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '8px' }}>
                        {placedEffect.effect.type}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                        {placedEffect.quantity || 1}
                      </td>
                      <td style={{ padding: '8px' }}>
                        <span style={{ 
                          color: isVariantValue(placedEffect, 'range') ? '#3b82f6' : 'inherit',
                          fontWeight: isVariantValue(placedEffect, 'range') ? '600' : 'normal'
                        }}>
                          {getEffectValue(placedEffect, 'range') || getEffectValue(placedEffect, 'effective_range') || 'N/A'}m
                        </span>
                        {isVariantValue(placedEffect, 'range') && <span style={{ color: '#2563a8', marginLeft: '4px' }}>Modified</span>}
                      </td>
                      <td style={{ padding: '8px' }}>
                        <span style={{ 
                          color: isVariantValue(placedEffect, 'safety_distance') ? '#3b82f6' : 'inherit',
                          fontWeight: isVariantValue(placedEffect, 'safety_distance') ? '600' : 'normal'
                        }}>
                          {getEffectValue(placedEffect, 'safety_distance') || 'N/A'}m
                        </span>
                        {isVariantValue(placedEffect, 'safety_distance') && <span style={{ color: '#2563a8', marginLeft: '4px' }}>Modified</span>}
                      </td>
                      <td style={{ padding: '8px' }}>
                        <span style={{ 
                          color: isVariantValue(placedEffect, 'clearance_height') ? '#3b82f6' : 'inherit',
                          fontWeight: isVariantValue(placedEffect, 'clearance_height') ? '600' : 'normal'
                        }}>
                          {getEffectValue(placedEffect, 'clearance_height') || 'N/A'}m
                        </span>
                        {isVariantValue(placedEffect, 'clearance_height') && <span style={{ color: '#2563a8', marginLeft: '4px' }}>Modified</span>}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleRemoveEffect(placedEffect.id)}
                          title="Remove effect"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Risk Analysis Table - MAIN FOCUS - Takes remaining page space */}
        <div className="card" style={{ margin: '20px 0' }}>
          <div className="risk-entries-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: '0' }}>Risk Analysis Entries ({riskEntries.length})</h3>
            <div className="risk-entries-buttons" style={{ display: 'flex', gap: '15px' }}>
              <div className="risk-entries-row-1">
                <button 
                  className="btn btn-success btn-sm"
                  onClick={addManualRiskEntry}
                  title="Add manual risk entry"
                >
                  <Plus size={16} className="mr-2" />
                  Add Entry
                </button>
                <button 
                  className="btn btn-danger btn-sm"
                  onClick={clearAllRiskEntries}
                  disabled={riskEntries.length === 0}
                  title="Clear all risk entries"
                >
                  <Trash2 size={16} className="mr-2" />
                  Clear All Entries
                </button>
              </div>
              <div className="risk-entries-row-2">
                <button 
                  className="btn btn-sm"
                  onClick={generateRiskEntries}
                  disabled={placedEffects.length === 0}
                  title="Generate risk entries based on the effects in this event"
                >
                  <Plus size={16} className="mr-2" />
                  Generate Effect Based Risk Entries
                </button>
              </div>
            </div>
          </div>
          
          {riskEntries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <p>No risk entries yet.</p>
              <p>Click "Add Entry" to manually add entries or "Generate Risk Entries" to auto-generate from effects.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-striped" style={{ minWidth: '1000px', fontSize: '14px' }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <tr>
                    <th style={{ width: '60px', padding: '8px 4px', textAlign: 'center' }}>Delete</th>
                    <th style={{ width: '120px', padding: '8px 6px' }}>Aktivitet</th>
                    <th style={{ width: '150px', padding: '8px 6px' }}>Faremoment</th>
                    <th style={{ width: '130px', padding: '8px 6px' }}>Årsak</th>
                    <th style={{ width: '90px', padding: '8px 6px' }}>Sannsynlighet</th>
                    <th style={{ width: '90px', padding: '8px 6px' }}>Konsekvens</th>
                    <th style={{ width: '200px', padding: '8px 6px' }}>Tiltak</th>
                    <th style={{ width: '100px', padding: '8px 6px' }}>Risikonivå etter tiltak</th>
                  </tr>
                </thead>
                <tbody>
                  {riskEntries.map((entry, index) => (
                    <RiskEntryRow
                      key={entry.id}
                      entry={entry}
                      index={index}
                      onUpdate={(updatedEntry) => {
                        const updatedEntries = [...riskEntries];
                        updatedEntries[index] = updatedEntry;
                        setRiskEntries(updatedEntries);
                      }}
                      onDelete={(deletedId) => {
                        setRiskEntries(riskEntries.filter(entry => entry.id !== deletedId));
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>


    </div>
  );
};

export default RiskAnalysis;
